import { useCallback, useMemo } from 'react';
import type {
  OperationDto,
  OperationStatusIcon,
  OperationTypeIcon,
} from '@/shared/types';
import { LabeledIcon } from '@/shared/components';
import { capitalize, cn } from '@/shared/utils';
import { formatInTimeZone } from 'date-fns-tz';
import { formatReceivedAmount, useDateLocale } from '@/shared/lib';
import {
  DATE_TIME_DISPLAY_FORMAT,
  HIDDEN_BALANCES_FULL_TEXT,
} from '@/shared/config';

const COMPACT_DATE_TIME_FORMAT = 'MMM dd, HH:mm';

type Props = {
  isBalancesHidden?: boolean;
  operation: OperationDto;
  operationStatusIcons: Array<OperationStatusIcon>;
  operationTypeIcons: Array<OperationTypeIcon>;
  onOperationItemClick?: (operation: OperationDto) => void;
  variant?: 'default' | 'compact';
  isSelected?: boolean;
  className?: string;
  statusClassName?: string;
};

export const OperationsHistoryItem = ({
  isBalancesHidden,
  operation,
  operationStatusIcons,
  operationTypeIcons,
  onOperationItemClick,
  variant = 'default',
  isSelected = false,
  className,
  statusClassName,
}: Props) => {
  const isCompact = variant === 'compact';

  const { dateLocale } = useDateLocale();

  const operationStatusIcon = operationStatusIcons.find(
    icon => icon.status === operation.status
  );
  const operationTypeIcon = operationTypeIcons.find(
    icon => icon.operationType === operation.operationType
  );

  const operationDate = useMemo(
    () =>
      formatInTimeZone(
        operation.date,
        'UTC',
        isCompact ? COMPACT_DATE_TIME_FORMAT : DATE_TIME_DISPLAY_FORMAT,
        { locale: dateLocale }
      ),
    [operation.date, dateLocale, isCompact]
  );

  const handleOperationItemClick = useCallback(() => {
    onOperationItemClick?.(operation);
  }, [operation, onOperationItemClick]);

  const amountRows = useMemo(() => {
    const main = {
      direction: operation.direction,
      amount: formatReceivedAmount(operation.amount),
      code: operation.asset.code,
      isAdditional: false,
    };

    if (
      operation.additionalAmount &&
      operation.additionalAsset &&
      operation.additionalDirection
    ) {
      const additional = {
        direction: operation.additionalDirection,
        amount: formatReceivedAmount(operation.additionalAmount),
        code: operation.additionalAsset.code,
        isAdditional: true,
      };

      return [main, additional];
    }

    return [main];
  }, [operation]);

  const mainSign = amountRows[0].direction === 'credit' ? '+' : '-';

  return (
    <div
      className={cn(
        'flex items-center gap-[1.6rem] group hover:bg-secondary-bg cursor-pointer w-full',
        isCompact
          ? 'items-start px-[1.6rem] py-[1.2rem] rounded-[1.6rem] border border-transparent'
          : 'py-[1.4rem] rounded-4xl px-0 md:px-[1.6rem]',
        isCompact &&
          isSelected &&
          'bg-background hover:bg-background border-stroke-bg shadow-[0_0.4rem_0.4rem_0_rgba(0,0,0,0.04)]',
        className
      )}
      onClick={handleOperationItemClick}>
      {operationTypeIcon?.icon && operationStatusIcon?.icon && (
        <LabeledIcon
          icon={operationTypeIcon?.icon}
          label={operationStatusIcon?.icon}
          iconClassName={cn(
            'dark:brightness-0 dark:invert',
            isCompact && 'size-[2.4rem]'
          )}
          className={cn(
            'border-none bg-secondary-bg group-hover:bg-stroke',
            isCompact ? 'size-[3.8rem] shrink-0' : 'w-[4.4rem] h-[4.4rem]'
          )}
          labelClassName={cn(
            'border-[0.2rem] border-white group-hover:border-secondary-bg rounded-full',
            isCompact
              ? 'size-[1.6rem] -bottom-[0.2rem] -right-[0.2rem]'
              : 'w-[1.8rem] h-[1.8rem]',
            statusClassName
          )}
        />
      )}
      <div
        className={cn(
          'grow flex flex-col',
          isCompact ? 'min-w-0 gap-0' : 'gap-[0.2rem]'
        )}>
        <div
          className={cn(
            'flex items-center justify-between',
            isCompact && 'gap-[1.6rem]'
          )}>
          <h4
            className={cn(
              'text-primary',
              isCompact ? 'medium5 truncate' : 'medium4'
            )}>
            {operation.operationSubTypeName}
          </h4>

          <div
            className={cn(
              'text-end',
              isCompact
                ? 'medium5 whitespace-nowrap shrink-0'
                : 'medium4 text-primary',
              isCompact &&
                (mainSign === '+' && !isBalancesHidden
                  ? 'text-primary-cyan'
                  : 'text-primary')
            )}>
            {isBalancesHidden ? (
              HIDDEN_BALANCES_FULL_TEXT
            ) : (
              <>
                {mainSign} {amountRows[0].amount}
              </>
            )}{' '}
            {amountRows[0].code}
          </div>
        </div>
        <div
          className={cn(
            'flex items-center justify-between',
            isCompact && 'gap-[1.6rem]'
          )}>
          <p className={cn('text-secondary', isCompact ? 'p6 truncate' : 'p5')}>
            {capitalize(operationDate)}
          </p>
          {amountRows[1] && (
            <div
              className={cn(
                'text-secondary text-end whitespace-nowrap',
                isCompact ? 'p6 shrink-0' : 'p5'
              )}>
              {isBalancesHidden ? (
                HIDDEN_BALANCES_FULL_TEXT
              ) : (
                <>
                  {amountRows[1].direction === 'credit' ? '+' : '-'}{' '}
                  {amountRows[1].amount}
                </>
              )}{' '}
              {amountRows[1].code}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
